import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import BrandLogo from '@/components/common/BrandLogo';
import LanguageMenu from '@/components/common/LanguageMenu';

/** Sections of the home page, grouped under "Product". */
const productLinks = [
  { label: 'header.solutions', href: '#solutions', icon: 'ri-lightbulb-flash-line', text: 'header.solutionsText' },
  { label: 'header.features', href: '#products', icon: 'ri-apps-2-line', text: 'header.featuresText' },
  { label: 'header.customers', href: '#customers', icon: 'ri-user-heart-line', text: 'header.customersText' },
  { label: 'header.security', href: '#security', icon: 'ri-shield-check-line', text: 'header.securityText' },
];

/** Pages with their own route. */
const pageLinks = [
  { label: 'header.advertise', href: '/advertise' },
  { label: 'header.pricing', href: '/pricing' },
];

export default function Header() {
  const { pathname } = useLocation();
  const { t } = useTranslation();
  // Section links (#...) live on the home page; from other pages, go home first.
  const hrefFor = (href: string) => (href.startsWith('#') && pathname !== '/' ? `${import.meta.env.BASE_URL}${href}` : href);
  const [productOpen, setProductOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const productRef = useRef<HTMLDivElement>(null);

  // Close the Product menu on outside click or Escape.
  useEffect(() => {
    if (!productOpen) return;
    const onClick = (e: MouseEvent) => {
      if (!productRef.current?.contains(e.target as Node)) setProductOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setProductOpen(false);
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [productOpen]);

  // Close menus after navigating.
  useEffect(() => {
    setProductOpen(false);
    setMenuOpen(false);
  }, [pathname]);

  const navItem = 'h-9 px-3 rounded-full text-[15px] text-[#1f2329] hover:bg-[#f2f3f5] transition-colors inline-flex items-center';

  return (
    <header className="fixed top-0 inset-x-0 z-50 px-3 sm:px-4 pt-3">
      <nav className="relative max-w-6xl mx-auto h-14 pl-4 pr-2 rounded-2xl bg-white/90 backdrop-blur-md border border-[#e9ebef] shadow-[0_6px_24px_-12px_rgba(31,35,41,0.18)] grid grid-cols-[1fr_auto] lg:grid-cols-[1fr_auto_1fr] items-center">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 justify-self-start">
          <BrandLogo className="w-7 h-7" />
          <span className="font-heading font-bold text-[16px] text-[#1f2329]">LoyaltyBridge</span>
        </Link>

        {/* Centered menu */}
        <div className="hidden lg:flex items-center gap-1">
          <div ref={productRef} className="relative">
            <button
              type="button"
              onClick={() => setProductOpen((o) => !o)}
              aria-expanded={productOpen}
              aria-haspopup="true"
              className={`${navItem} gap-1 cursor-pointer ${productOpen ? 'bg-[#f2f3f5]' : ''}`}
            >
              {t('header.product')}
              <i className={`ri-arrow-down-s-line text-lg leading-none transition-transform ${productOpen ? 'rotate-180' : ''}`} />
            </button>
            {productOpen && (
              <div className="absolute left-1/2 -translate-x-1/2 top-12 w-[340px] rounded-2xl bg-white border border-[#e9ebef] shadow-[0_20px_50px_-20px_rgba(31,35,41,0.35)] p-2">
                {productLinks.map((l) => (
                  <a
                    key={l.href}
                    href={hrefFor(l.href)}
                    onClick={() => setProductOpen(false)}
                    className="flex items-start gap-3 rounded-xl px-3 py-2.5 hover:bg-[#f5f6f8] transition-colors"
                  >
                    <span className="w-9 h-9 shrink-0 rounded-lg bg-[#f2f3f5] flex items-center justify-center text-[#1f2329]">
                      <i className={`${l.icon} text-lg`} />
                    </span>
                    <span>
                      <span className="block text-sm font-semibold text-[#1f2329]">{t(l.label)}</span>
                      <span className="block text-xs text-[#8f959e]">{t(l.text)}</span>
                    </span>
                  </a>
                ))}
              </div>
            )}
          </div>
          <a href={hrefFor('#customers')} className={navItem}>
            {t('header.customers')}
          </a>
          {pageLinks.map((l) => (
            <Link key={l.href} to={l.href} className={`${navItem} ${pathname === l.href ? 'font-semibold' : ''}`}>
              {t(l.label)}
            </Link>
          ))}
        </div>

        {/* Right side */}
        <div className="flex items-center gap-1 justify-self-end">
          <Link to="/login" className="hidden sm:inline-flex h-10 px-4 items-center text-[15px] text-[#1f2329] hover:text-[#2f51d6] transition-colors">
            {t('header.signIn')}
          </Link>
          <Link
            to="/signup"
            className="hidden sm:inline-flex h-10 px-5 items-center gap-2 rounded-xl bg-[#1f2329] text-white text-[15px] font-medium hover:bg-black transition-colors shadow-[0_6px_16px_-8px_rgba(31,35,41,0.8)]"
          >
            {t('header.startFree')} <i className="ri-arrow-right-up-line" />
          </Link>
          <LanguageMenu />
          <button
            type="button"
            onClick={() => setMenuOpen((o) => !o)}
            className="lg:hidden w-10 h-10 flex items-center justify-center rounded-xl text-[#1f2329] hover:bg-[#f2f3f5] cursor-pointer"
            aria-label={t('header.toggleMenu')}
            aria-expanded={menuOpen}
          >
            <i className={`${menuOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl`} />
          </button>
        </div>
      </nav>

      {/* Mobile menu */}
      {menuOpen && (
        <div className="lg:hidden max-w-6xl mx-auto mt-2 rounded-2xl bg-white border border-[#e9ebef] shadow-[0_20px_50px_-20px_rgba(31,35,41,0.35)] p-2">
          {productLinks.map((l) => (
            <a key={l.href} href={hrefFor(l.href)} onClick={() => setMenuOpen(false)} className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-[#f5f6f8]">
              <i className={`${l.icon} text-lg text-[#646a73]`} />
              <span className="text-sm font-medium text-[#1f2329]">{t(l.label)}</span>
            </a>
          ))}
          {pageLinks.map((l) => (
            <Link key={l.href} to={l.href} className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-[#f5f6f8]">
              <i className={`${l.href === '/pricing' ? 'ri-price-tag-3-line' : 'ri-megaphone-line'} text-lg text-[#646a73]`} />
              <span className="text-sm font-medium text-[#1f2329]">{t(l.label)}</span>
            </Link>
          ))}
          <div className="grid grid-cols-2 gap-2 p-1 pt-3 mt-1 border-t border-[#eff0f1]">
            <Link to="/login" className="h-10 inline-flex items-center justify-center rounded-xl border border-[#e2e4e8] text-sm font-medium text-[#1f2329]">
              {t('header.signIn')}
            </Link>
            <Link to="/signup" className="h-10 inline-flex items-center justify-center rounded-xl bg-[#1f2329] text-white text-sm font-medium">
              {t('header.startFree')}
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
